import type jsPDF from "jspdf";

import { drawWatermarkBrandMark } from "./brand-mark-pdf";


/**
 * Shared "PRELIMINARY RESULTS" stamping for every printed exhibit and exported
 * PDF/PNG. Three pieces:
 *
 *  - a large diagonal watermark across each page, drawn at low opacity so it is
 *    unmistakable but never hides parcel geometry or text,
 *  - on free-tier reports, a stronger Easements.com lockup plus a light tiled
 *    repeat of the wordmark, so a cropped page still carries the mark, and
 *  - a one-line footer reminding the reader that the document is preliminary.
 *
 * Every export path calls these so a printed page can never be mistaken for a
 * survey or a title opinion.
 */

export const PRELIMINARY_WATERMARK_TEXT = "PRELIMINARY RESULTS";
export const FREE_WATERMARK_LINES = ["EASEMENTS.COM", "FREE PRELIMINARY REPORT"] as const;
export const PRELIMINARY_FOOTER_LINE =
  "Preliminary results. Not a survey or title opinion. Verify with a licensed surveyor and title professional.";

/** Watermark opacity: visible on screen and in print, still fully readable under. */
const WATERMARK_ALPHA = 0.1;
/** Free reports carry a heavier mark. */
const FREE_WATERMARK_ALPHA = 0.16;
const FREE_TILE_ALPHA = 0.055;
/** Keep the rotated wordmark this far inside the trim on every side. */
const FIT_MARGIN = 24;

export type WatermarkTier = "free" | string | null | undefined;

export interface WatermarkOptions {
  /** Account tier the export was produced under. "free" gets the strong mark. */
  tier?: WatermarkTier;
}

interface GStateCapableDoc {
  GState?: (opts: { opacity: number }) => unknown;
  setGState?: (state: unknown) => void;
}

function setAlpha(doc: jsPDF, alpha: number) {
  const gs = doc as unknown as GStateCapableDoc;
  if (gs.GState && gs.setGState) gs.setGState(gs.GState({ opacity: alpha }));
}

/**
 * Largest font size at which `text`, rotated by `angleDeg`, still fits inside
 * a `W` x `H` page with a margin on every side.
 */
function fitRotatedSize(
  measure: (size: number) => number,
  angleDeg: number,
  W: number,
  H: number,
  opts: { start: number; min: number; max: number; lines?: number },
): number {
  const rad = (angleDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  const availW = Math.max(1, W - FIT_MARGIN * 2);
  const availH = Math.max(1, H - FIT_MARGIN * 2);
  const lines = opts.lines ?? 1;

  let size = opts.start;
  for (let i = 0; i < 40; i += 1) {
    const w = measure(size);
    const h = size * 1.2 * lines;
    const boxW = w * c + h * s;
    const boxH = w * s + h * c;
    if (boxW <= availW && boxH <= availH) break;
    const scale = Math.min(availW / boxW, availH / boxH);
    const next = Math.max(opts.min, size * scale * 0.98);
    if (next >= size) break;
    size = next;
  }
  return Math.max(opts.min, Math.min(opts.max, size));
}

/**
 * Draws one rotated line centered on (cx, cy). jsPDF applies text alignment
 * before rotation, so the origin is offset manually along the rotation vector
 * instead of relying on `align: "center"` (which pushes the ends off the page).
 */
function drawRotatedCentered(
  doc: jsPDF,
  text: string,
  cx: number,
  cy: number,
  angleDeg: number,
  size: number,
) {
  const rad = (angleDeg * Math.PI) / 180;
  doc.setFontSize(size);
  const w = doc.getTextWidth(text);
  // Rotation is counter-clockwise on a y-down page: +x advances (cos, -sin).
  const x = cx - (w / 2) * Math.cos(rad);
  const y = cy + (w / 2) * Math.sin(rad);
  doc.text(text, x, y, { baseline: "middle", angle: angleDeg });
}

/** Light tiled wordmark behind the content, used on free-tier exports. */
function tileWordmark(doc: jsPDF, W: number, H: number, angleDeg: number) {
  const size = 11;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size);
  doc.setTextColor(150, 150, 150);
  setAlpha(doc, FREE_TILE_ALPHA);
  const stepX = 170;
  const stepY = 120;
  for (let row = 0, y = 70; y < H - 40; y += stepY, row += 1) {
    const offset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = 40 + offset; x < W - 40; x += stepX) {
      drawRotatedCentered(doc, "EASEMENTS.COM", x, y, angleDeg, size);
    }
  }
  setAlpha(doc, 1);
}

/**
 * Draws the diagonal preliminary watermark across every page of a jsPDF
 * document. Safe to call once, at the very end of a build, after all content
 * and footers are in place.
 */
export function stampPreliminaryWatermark(doc: jsPDF, opts: WatermarkOptions = {}): void {
  const total = doc.getNumberOfPages();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const free = opts.tier === "free";
  const angle = Math.round((Math.atan2(H, W) * 180) / Math.PI);

  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);

    if (free) tileWordmark(doc, W, H, angle);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(free ? 120 : 140, free ? 120 : 140, free ? 120 : 140);

    const lines = free ? [...FREE_WATERMARK_LINES] : [PRELIMINARY_WATERMARK_TEXT];
    const longest = lines.reduce((a, b) => (a.length >= b.length ? a : b), "");
    const size = fitRotatedSize(
      (s) => {
        doc.setFontSize(s);
        return doc.getTextWidth(longest);
      },
      angle,
      W,
      H,
      { start: 72, min: 22, max: 120, lines: lines.length },
    );

    setAlpha(doc, free ? FREE_WATERMARK_ALPHA : WATERMARK_ALPHA);
    const rad = (angle * Math.PI) / 180;
    const lineGap = size * 1.15;
    lines.forEach((line, i) => {
      // Stack extra lines perpendicular to the diagonal so they stay parallel.
      const offset = (i - (lines.length - 1) / 2) * lineGap;
      const cx = W / 2 + offset * Math.sin(rad);
      const cy = H / 2 + offset * Math.cos(rad);
      drawRotatedCentered(doc, line, cx, cy, angle, size);
    });
    if (free) {
      // Site logo tile sits above the lockup, on the same diagonal. Keep it
      // small and faint so it reads as part of the watermark, not as content.
      const markSize = size * 0.55;
      const markOffset = -(lines.length / 2) * lineGap - markSize * 0.95;
      drawWatermarkBrandMark(
        doc,
        W / 2 + markOffset * Math.sin(rad),
        H / 2 + markOffset * Math.cos(rad),
        markSize,
        angle,
      );
    }

    setAlpha(doc, 1);
  }


  doc.setPage(total);
  doc.setTextColor(0, 0, 0);
}

/** Canvas equivalent for PNG exhibit exports. */
export function drawPreliminaryWatermarkOnCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  opts: WatermarkOptions = {},
): void {
  const free = opts.tier === "free";
  const lines = free ? [...FREE_WATERMARK_LINES] : [PRELIMINARY_WATERMARK_TEXT];
  const longest = lines.reduce((a, b) => (a.length >= b.length ? a : b), "");
  const angle = Math.atan2(height, width);

  const size = fitRotatedSize(
    (s) => {
      ctx.font = `bold ${s}px Helvetica, Arial, sans-serif`;
      return ctx.measureText(longest).width;
    },
    (angle * 180) / Math.PI,
    width,
    height,
    { start: 72, min: 18, max: Math.max(24, width / 4), lines: lines.length },
  );

  ctx.save();
  ctx.font = `bold ${size}px Helvetica, Arial, sans-serif`;
  ctx.globalAlpha = free ? FREE_WATERMARK_ALPHA : WATERMARK_ALPHA;
  ctx.fillStyle = free ? "#787878" : "#8c8c8c";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.translate(width / 2, height / 2);
  ctx.rotate(-angle);
  const lineGap = size * 1.15;
  lines.forEach((line, i) => {
    ctx.fillText(line, 0, (i - (lines.length - 1) / 2) * lineGap);
  });
  ctx.restore();
}
